import { useEffect, useState } from 'react';
import { boot, watchSession, type AppUser, type BootState } from './boot/session';
import { useRoute } from './boot/router';
import { devPreviewUser } from './boot/devPreview';
import { goShellLogin } from './data/supabase';
import { Navbar } from './shell/Navbar';
import { Sidebar } from './shell/Sidebar';
import { ErrorState, LoadingState } from './shell/State';
import { renderRoute } from './pages';

export default function App() {
  // 로컬 개발 전용: DB 준비 전에도 셸/라우팅을 볼 수 있게 한다. localhost 외에서는 null.
  const preview = devPreviewUser();

  const [state, setState] = useState<BootState>(
    preview ? { status: 'ready', user: preview } : { status: 'loading' }
  );

  useEffect(() => {
    if (preview) return;
    let alive = true;
    void boot().then((s) => {
      if (alive) setState(s);
    });
    // 다른 탭에서 로그아웃하거나 세션이 만료되면 쉘로 돌려보낸다.
    const stop = watchSession(goShellLogin);
    return () => {
      alive = false;
      stop();
    };
  }, [preview]);

  if (state.status === 'loading') {
    return <LoadingState label="세션 확인 중…" />;
  }

  if (state.status === 'redirecting') {
    return <LoadingState label="ONE 플랫폼으로 이동 중…" />;
  }

  if (state.status === 'inactive') {
    return (
      <ErrorState
        title="사용이 중지된 계정입니다"
        message={`${state.email} 계정은 StoreCAD 에서 비활성 상태입니다. CAD 관리자에게 문의하세요.`}
      />
    );
  }

  if (state.status === 'error') {
    return (
      <ErrorState message={state.message} onRetry={() => window.location.reload()} />
    );
  }

  return <Shell user={state.user} preview={preview !== null} />;
}

/**
 * 두 겹 구조 (통합표준 6절):
 *   바깥 #app-layout — 로그인 후 보이기/숨기기 담당
 *   안쪽 .layout    — 사이드바 + 메인 배치 담당
 * 이렇게 두면 표시줄까지 한 번에 함께 나타나고 사라진다.
 */
function Shell({ user, preview }: { user: AppUser; preview: boolean }) {
  const route = useRoute();
  const isEditor = route.name === 'drawing' || route.name === 'version' || route.name === 'compare';

  return (
    <div id="app-layout">
      {preview && (
        <div className="dev-banner" role="status">
          개발 미리보기 모드 — 실제 로그인/데이터가 아닙니다 (localhost 전용, 역할:{' '}
          {user.role})
        </div>
      )}
      <Navbar />
      <div className="layout">
        <Sidebar user={user} active={route.name} />
        <div className={isEditor ? 'main main--fixed' : 'main'}>{renderRoute(route, user)}</div>
      </div>
    </div>
  );
}
